import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraOverlayDialog } from '@mono/ui-components/captains';
import { SpecraButton } from '@mono/ui-components/ensigns';
import { SpecraInlineBanner } from '@mono/ui-components/lieutenants';
import { Testids } from '@mono/ui-selectors';
import { useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useTenantAreas } from '../../../lib/access/useTenantAreas';
import { PricingSheetIngestProgress } from '../../Delivery/Registries/PricingSheetIngestProgress';
import { PricingSheetUploadDialog } from '../../Delivery/Registries/PricingSheetUploadDialog';
import { useActiveIngests } from '../../Delivery/Registries/useActiveIngests';
import { useCanUploadPricingSheet } from '../../Delivery/Registries/usePricingSheetUploadGate';
import { vehicleConnectionHref } from '../../Delivery/Registries/VehicleConnectionsSection';
import { VehicleProfileEditor } from '../../Delivery/Registries/VehicleProfileEditor';
import { useVehicleConnections, useVehicleProfile, useVehicleViewer } from '../../Delivery/Registries/vehicleIdentityHooks';
import { useRecordDocuments } from '../documents/hooks';
import { documentsQueryState } from '../documents/recordDocumentsSection';
import { RegistryRecordFrame } from '../RegistryRecordFrame';
import { HoldersTable } from './vehicle/HoldersTable';
import { HoldingsDialog } from './vehicle/HoldingsDialog';
import { useVehicleDetails, useVehicleDossier } from './vehicle/hooks';
import { LcatsOnVehicle } from './vehicle/LcatsOnVehicle';
import { LineageEditorDialog } from './vehicle/LineageEditorDialog';
import { PricingSheetsKept } from './vehicle/PricingSheetsKept';
import { TaskOrderRounds } from './vehicle/TaskOrderRounds';
import { VehicleHeader } from './vehicle/VehicleHeader';
import { vehicleRail } from './vehicle/vehicleRail';
/**
 * "Edit profile" lives in the header (mockup: no profile block on the page). It opens the
 * existing VehicleProfileEditor in a dialog. System-seeded vehicles are read-only.
 */ function useVehicleProfileEdit(id, onSaved) {
    const profile = useVehicleProfile(id);
    const canWrite = useTenantAreas().registries;
    const [open, setOpen] = useState(false);
    const editable = canWrite && !!profile.data?.vehicle && !profile.data.vehicle.isSystemSeed;
    const button = editable ? /*#__PURE__*/ _jsx(SpecraButton, {
        variant: "secondary",
        "data-testid": Testids.registryVehicle.editProfile,
        onClick: ()=>setOpen(true),
        children: "Edit profile"
    }) : null;
    const dialog = editable && profile.data ? /*#__PURE__*/ _jsx(SpecraOverlayDialog, {
        open: open,
        onOpenChange: setOpen,
        title: "Edit vehicle profile",
        size: "lg",
        children: /*#__PURE__*/ _jsx(VehicleProfileEditor, {
            profile: profile.data,
            onCancel: ()=>setOpen(false),
            onSaved: ()=>{
                setOpen(false);
                onSaved();
            },
            onReload: async ()=>{
                await profile.refetch();
                setOpen(false);
            }
        }, String(profile.data.vehicle?.revision))
    }) : null;
    return {
        button,
        dialog
    };
}
export function VehicleRecordPage() {
    const { id = '' } = useParams();
    return /*#__PURE__*/ _jsx(VehicleRecord, {
        id: id
    }, id);
}
function VehicleRecord({ id }) {
    const query = useVehicleDossier(id);
    const d = query.data;
    const year = new Date().getUTCFullYear();
    const { holders, rounds } = useVehicleDetails(id, !!d, year);
    const documents = useRecordDocuments({
        kind: 'VEHICLE',
        id
    }, !!d);
    const canWrite = useTenantAreas().registries;
    const canUpload = useCanUploadPricingSheet();
    const [lineageOpen, setLineageOpen] = useState(false);
    const [uploadOpen, setUploadOpen] = useState(false);
    const [holdingsOpen, setHoldingsOpen] = useState(false);
    const viewer = useVehicleViewer();
    const { runs, track, untrack } = useActiveIngests(viewer.tenant);
    const profileEdit = useVehicleProfileEdit(id, ()=>void query.refetch());
    const location = useLocation();
    const origin = location.pathname + location.search;
    const programConnections = useVehicleConnections(id, 'program');
    const programs = (programConnections.data ?? []).map((r)=>({
            id: `${r.kind}:${r.id}`,
            label: r.label || r.id,
            href: vehicleConnectionHref(r, id, origin)
        }));
    return /*#__PURE__*/ _jsx(RegistryRecordFrame, {
        kindLabel: "Contract Vehicles",
        tab: "vehicles",
        title: d?.name,
        status: query.isPending ? 'loading' : query.isError ? 'error' : d ? 'ready' : 'unavailable',
        error: query.error,
        onRetry: ()=>void query.refetch(),
        header: d && /*#__PURE__*/ _jsx(VehicleHeader, {
            dossier: d,
            onUpload: canUpload ? ()=>setUploadOpen(true) : undefined,
            extraActions: profileEdit.button
        }),
        railSections: d ? vehicleRail(d, holders.data, canWrite ? ()=>setLineageOpen(true) : undefined, documents.data, documentsQueryState(documents), programs) : undefined,
        children: d && /*#__PURE__*/ _jsxs("div", {
            className: "space-y-8",
            "data-testid": Testids.registryVehicle.page,
            children: [
                rounds.isPending ? /*#__PURE__*/ _jsx("output", {
                    children: "Loading task-order rounds…"
                }) : rounds.isError ? /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                    tone: "danger",
                    action: /*#__PURE__*/ _jsx(SpecraButton, {
                        onClick: ()=>void rounds.refetch(),
                        children: "Retry rounds"
                    }),
                    children: "Could not load task-order rounds."
                }) : /*#__PURE__*/ _jsx(TaskOrderRounds, {
                    data: rounds.data,
                    dossier: d,
                    year: year
                }),
                holders.isPending ? /*#__PURE__*/ _jsx("output", {
                    children: "Loading holders…"
                }) : holders.isError ? /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                    tone: "danger",
                    action: /*#__PURE__*/ _jsx(SpecraButton, {
                        onClick: ()=>void holders.refetch(),
                        children: "Retry holders"
                    }),
                    children: "Could not load holders."
                }) : /*#__PURE__*/ _jsx(HoldersTable, {
                    data: holders.data,
                    name: d.name,
                    action: canWrite && /*#__PURE__*/ _jsx(SpecraButton, {
                        size: "xs",
                        variant: "secondary",
                        "data-testid": Testids.registryVehicle.recordHolding,
                        onClick: ()=>setHoldingsOpen(true),
                        children: "Record holding"
                    })
                }),
                /*#__PURE__*/ _jsx(PricingSheetsKept, {
                    sheets: d.pricingSheets
                }),
                /*#__PURE__*/ _jsx(LcatsOnVehicle, {
                    dossier: d
                }),
                canWrite && /*#__PURE__*/ _jsx(HoldingsDialog, {
                    vehicleId: id,
                    vehicleName: d.name,
                    open: holdingsOpen,
                    onOpenChange: setHoldingsOpen
                }),
                profileEdit.dialog,
                lineageOpen && canWrite && /*#__PURE__*/ _jsx(LineageEditorDialog, {
                    vehicleId: id,
                    canWrite: canWrite,
                    onClose: ()=>setLineageOpen(false)
                }),
                canUpload && /*#__PURE__*/ _jsx(PricingSheetUploadDialog, {
                    open: uploadOpen,
                    onOpenChange: setUploadOpen,
                    expectedVehicleId: id,
                    expectedVehicleName: d.name,
                    onStarted: (runId, filename)=>{
                        setUploadOpen(false);
                        track(runId, filename);
                    }
                }),
                runs.map((run)=>/*#__PURE__*/ _jsx(PricingSheetIngestProgress, {
                        runId: run.runId,
                        filename: run.filename,
                        onSettled: ()=>{
                            untrack(run.runId);
                            void query.refetch();
                        }
                    }, run.runId))
            ]
        })
    });
}
